import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Link,
} from '@chakra-ui/react';

const SUPPORT_EMAIL = 'contact@email-cleaner.app';

const StepBadge = ({ children }) => (
  <Box
    w="32px"
    h="32px"
    borderRadius="full"
    bg="#52c41a"
    color="white"
    display="flex"
    alignItems="center"
    justifyContent="center"
    fontWeight="bold"
    fontSize="sm"
    flexShrink={0}
  >
    {children}
  </Box>
);

const RefundPolicy = () => {
  const headingColor = 'gray.900';
  const textColor = 'gray.600';
  const linkColor = '#135200';

  const emailLink = (label) => (
    <Link
      href={`mailto:${SUPPORT_EMAIL}`}
      color={linkColor}
      textDecoration="underline"
      _hover={{ color: '#389e0d' }}
    >
      {label || SUPPORT_EMAIL}
    </Link>
  );

  const paddleLink = (label, href) => (
    <Link
      href={href}
      isExternal
      color={linkColor}
      textDecoration="underline"
      _hover={{ color: '#389e0d' }}
    >
      {label}
    </Link>
  );

  return (
    <Box as="main" minH="100vh" bg="gray.50" pt={20} pb={20}>
      <Container maxW="container.lg">
        <VStack spacing={10} align="start">
          {/* Page Header */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              size="2xl"
              color={headingColor}
              fontWeight="normal"
              lineHeight="1.15"
              letterSpacing="-0.01em"
            >
              Refund Policy
            </Heading>
            <Text fontSize="lg" color={textColor} lineHeight="relaxed" maxW="65ch">
              We want you to be confident subscribing to Email Cleaner. If it doesn't
              deliver value, we don't want your money. This policy sets out when and how
              you can get a refund.
            </Text>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderLeft="3px solid #52c41a"
              borderRadius="md"
              px={5}
              py={4}
              w="full"
            >
              <Text fontSize="md" color={textColor} lineHeight="relaxed">
                <Text as="span" fontWeight="semibold" color="gray.700">Short version: </Text>
                Request a refund within 14 days of your purchase or most recent renewal by
                emailing {emailLink()}, or contact Paddle directly at{' '}
                {paddleLink('paddle.net', 'https://paddle.net')}. That's it.
              </Text>
            </Box>
          </VStack>

          {/* How Refunds Work with Paddle */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              How refunds work with Paddle
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              All payments for Email Cleaner are processed by Paddle.com Market Limited
              ("Paddle"), who acts as the Merchant of Record. This means Paddle is the legal
              seller and the entity that charges your payment method, so refunds are issued
              by Paddle rather than directly by us.
            </Text>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              You can request a refund either by contacting us or by contacting Paddle
              directly through their buyer support portal at{' '}
              {paddleLink('paddle.net', 'https://paddle.net')}. Either way, the refund is
              processed through Paddle.
            </Text>
          </VStack>

          {/* Eligibility */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              Eligibility
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              You are eligible for a full refund if you request it within{' '}
              <Text as="span" fontWeight="semibold" color="gray.700">14 calendar days</Text>{' '}
              of your initial subscription purchase date, or within 14 calendar days of your
              most recent subscription renewal. This applies to new subscriptions and renewals.
            </Text>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              This aligns with Paddle's standard refund window. Final refund decisions are
              made by Paddle in accordance with their{' '}
              {paddleLink('Checkout Buyer Terms', 'https://www.paddle.com/legal/checkout-buyer-terms')},
              but we will always support legitimate refund requests made within this window.
            </Text>
          </VStack>

          {/* How to Request a Refund */}
          <VStack spacing={5} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              How to request a refund
            </Heading>

            <HStack spacing={4} align="flex-start" w="full">
              <StepBadge>1</StepBadge>
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={1}>
                  Contact us or Paddle
                </Text>
                <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="60ch">
                  Send a refund request to {emailLink()} with the subject line "Refund
                  Request" and include the email address you used to subscribe. Alternatively,
                  request a refund directly through Paddle's buyer support at{' '}
                  {paddleLink('paddle.net', 'https://paddle.net')}.
                </Text>
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-start" w="full">
              <StepBadge>2</StepBadge>
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={1}>
                  We confirm and forward to Paddle
                </Text>
                <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="60ch">
                  If you contact us directly, we will confirm receipt within 1 business day
                  and initiate the refund through Paddle. No questions asked within the
                  14-day window.
                </Text>
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-start" w="full">
              <StepBadge>3</StepBadge>
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={1}>
                  Paddle processes the refund
                </Text>
                <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="60ch">
                  Refunds typically appear on your original payment method within 5–10
                  business days, depending on your bank or card issuer. You will receive a
                  confirmation email from Paddle once the refund has been issued.
                </Text>
              </Box>
            </HStack>
          </VStack>

          {/* What Happens After a Refund */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              What happens after a refund
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Once your refund is processed, your subscription is cancelled immediately and
              access to the paid features your subscription unlocked will end. You will
              receive a confirmation email from Paddle once the refund has been issued.
            </Text>
          </VStack>

          {/* Cancellations Without Refund (moved from FAQ) */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              Cancellations without a refund
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              If you cancel outside the 14-day refund window, your access continues until the
              end of your current billing period. You will not be charged for subsequent
              periods, but the remaining paid period is non-refundable. For example, if you
              purchase a yearly plan and cancel 9 months later, you'll keep access to the paid
              features for the remaining 3 months, and won't be billed again after that.
            </Text>
          </VStack>

          {/* How to Cancel (moved from FAQ) */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              How to cancel your subscription
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Log in at{' '}
              <Link
                href="https://email-cleaner.app/profile"
                color={linkColor}
                textDecoration="underline"
                _hover={{ color: '#389e0d' }}
              >
                email-cleaner.app/profile
              </Link>
              {' '}with the account you used to buy your subscription, click "Manage your
              subscription", log in on customer-portal.paddle.com, select your subscription,
              and cancel it.
            </Text>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Alternatively, email Paddle at{' '}
              <Link
                href="mailto:help@paddle.com"
                color={linkColor}
                textDecoration="underline"
                _hover={{ color: '#389e0d' }}
              >
                help@paddle.com
              </Link>
              {' '}with the email address you used to purchase and your order number. You can
              find your order number on the receipt email you received when you paid, on a line
              that looks like Order number: #1234567.
            </Text>
          </VStack>

          {/* Contact */}
          <VStack spacing={4} align="start" w="full">
            <Heading size="lg" color={headingColor} fontWeight="normal">
              Questions?
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              If you have any questions about this Refund Policy or need help with a refund or
              cancellation, reach us at {emailLink()} and we'll be glad to help.
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default RefundPolicy;
